import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ArrowLeft,
    ArrowUpRight,
    ExternalLink,
    Lock,
    Eye,
    EyeOff,
    Linkedin,
    Instagram,
} from 'lucide-react';
import { caseStudies, author } from '@/data/caseStudies';
import { CASE_STUDY } from '@/constants/testIds';
import { proxyImg } from '@/lib/format';
import { RichBlock } from './case-study/RichBlocks';
import ReadingProgress from '@/components/portfolio/ReadingProgress';
import { CaseStudyChapterProvider } from '@/lib/caseStudyChapter';
import { estimateReadingMinutes } from '@/lib/readingTime';
import { Seo, SITE_URL, clampDescription, caseStudySchema } from '@/lib/seo';

const PASSCODE = '3394';
const STORAGE_KEY = 'ht-case-unlocked';

const useCaseUnlock = () => {
    // Initialise synchronously to prevent gate flash on refresh
    const [unlocked, setUnlocked] = useState(() => {
        try {
            return sessionStorage.getItem(STORAGE_KEY) === '1';
        } catch {
            return false;
        }
    });
    const unlock = () => {
        try {
            sessionStorage.setItem(STORAGE_KEY, '1');
        } catch {
            /* storage unavailable, noop */
        }
        setUnlocked(true);
    };
    return { unlocked, unlock };
};

const Gate = ({
    title,
    subtitle,
    category,
    overview,
    timeline,
    client,
    responsibilities,
    onUnlock,
}) => {
    const [value, setValue] = useState('');
    const [show, setShow] = useState(false);
    const [error, setError] = useState(false);

    const onSubmit = (e) => {
        e.preventDefault();
        if (value.trim() === PASSCODE) {
            onUnlock();
        } else {
            setError(true);
            setTimeout(() => setError(false), 900);
        }
    };

    return (
        <article className="relative pt-24 md:pt-28 pb-16 min-h-[100svh]">
            <div className="mx-auto max-w-[1100px] px-4 md:px-8 mb-6 md:mb-10 flex flex-col items-start gap-3 lg:flex-row lg:items-center lg:justify-between">
                <Link
                    to="/work"
                    data-testid={CASE_STUDY.backHome}
                    data-cursor="hover"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--line-2)] text-[13px] text-[var(--ink)] hover:bg-white/5 transition-colors shrink-0 whitespace-nowrap"
                >
                    <ArrowLeft size={14} /> Back to work
                </Link>
                <div className="flex flex-wrap items-center gap-2">
                    <span className="chip">
                        {category}
                    </span>
                    <span className="chip">
                        <Lock size={10} className="text-[var(--accent)]" />
                        Protected
                    </span>
                </div>
            </div>

            <div className="mx-auto max-w-[1100px] px-4 md:px-8">
                <h1
                    data-testid={CASE_STUDY.title}
                    className="font-display leading-[0.92] tracking-tight text-5xl md:text-7xl lg:text-[6.5vw] text-[var(--ink)] text-center mb-8 md:mb-12"
                >
                    {title.split(' ').slice(0, -1).join(' ') || title}
                    {title.split(' ').length > 1 && (
                        <>
                            {' '}
                            <span className="font-display-italic text-[var(--accent)]">
                                {title.split(' ').slice(-1)[0]}
                            </span>
                        </>
                    )}
                </h1>

                {/* PUBLIC SUMMARY, always visible above the passcode gate.
                    Recruiters can size up the context before deciding whether
                    to ask for the passcode. */}
                {(subtitle || overview || timeline || client) && (
                    <div className="mx-auto max-w-3xl mb-6 md:mb-10">
                        {subtitle && (
                            <p className="text-center font-display-italic text-xl md:text-2xl leading-[1.3] text-[var(--ink-2)] mb-8">
                                {subtitle}
                            </p>
                        )}
                        <div className="rounded-[22px] border border-[var(--line)] bg-[var(--surface)] p-6 md:p-8">
                            <div className="font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--accent)] mb-3">
                                Public summary
                            </div>
                            {overview && (
                                <p className="font-sans-brand text-[14.5px] md:text-[15px] leading-[1.65] text-[var(--ink-2)]">
                                    {overview}
                                </p>
                            )}
                            {(client || timeline || (responsibilities && responsibilities.length > 0)) && (
                                <dl className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4 pt-5 border-t border-[var(--line)]">
                                    {client && (
                                        <div>
                                            <dt className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                                                Client
                                            </dt>
                                            <dd className="mt-1 font-sans-brand text-[13px] text-[var(--ink)]">
                                                {client}
                                            </dd>
                                        </div>
                                    )}
                                    {timeline && (
                                        <div>
                                            <dt className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                                                Timeline &amp; role
                                            </dt>
                                            <dd className="mt-1 font-sans-brand text-[13px] text-[var(--ink)]">
                                                {timeline}
                                            </dd>
                                        </div>
                                    )}
                                    {responsibilities && responsibilities.length > 0 && (
                                        <div>
                                            <dt className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                                                Scope
                                            </dt>
                                            <dd className="mt-1 font-sans-brand text-[13px] text-[var(--ink)] leading-[1.5]">
                                                {responsibilities.slice(0, 3).join(' \u00b7 ')}
                                            </dd>
                                        </div>
                                    )}
                                </dl>
                            )}
                        </div>
                    </div>
                )}

                <motion.form
                    onSubmit={onSubmit}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{
                        opacity: 1,
                        y: 0,
                        x: error ? [0, -10, 10, -8, 8, -4, 4, 0] : 0,
                    }}
                    transition={{ duration: error ? 0.5 : 0.6 }}
                    data-testid="case-study-gate"
                    className="max-w-lg mx-auto rounded-[22px] border border-[var(--line-2)] bg-[var(--surface)] p-8 md:p-10 text-center"
                >
                    <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[var(--surface-2)] border border-[var(--line-2)] text-[var(--accent)] mb-6">
                        <Lock size={20} />
                    </span>
                    <div className="font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink-3)] mb-3">
                        Content protected
                    </div>
                    <h2 className="font-display text-2xl md:text-3xl leading-[1.15] text-[var(--ink)]">
                        Enter the passcode to view this case study.
                    </h2>
                    <p className="mt-3 max-w-sm mx-auto text-[14px] text-[var(--ink-2)]">
                        One passcode unlocks every case study for this session.
                        Reach out on LinkedIn or Instagram if you need it.
                    </p>

                    <div
                        className={`mt-6 flex items-center gap-2 rounded-full border px-4 py-1.5 transition-colors ${
                            error
                                ? 'border-red-500'
                                : 'border-[var(--line-2)] focus-within:border-[var(--accent)]'
                        }`}
                    >
                        <input
                            data-testid="case-study-passcode-input"
                            autoFocus
                            type={show ? 'text' : 'password'}
                            inputMode="numeric"
                            autoComplete="off"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            placeholder="Enter passcode"
                            className="flex-1 bg-transparent border-0 outline-none focus:outline-none focus-visible:outline-none text-[15px] font-sans-brand text-[var(--ink)] placeholder:text-[var(--ink-3)] py-2"
                        />
                        <button
                            type="button"
                            onClick={() => setShow((s) => !s)}
                            data-cursor="hover"
                            aria-label={show ? 'Hide passcode' : 'Show passcode'}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-full text-[var(--ink-3)] hover:text-[var(--ink)] transition-colors"
                        >
                            {show ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                    </div>

                    <button
                        type="submit"
                        data-testid="case-study-unlock-button"
                        data-cursor="hover"
                        className="mt-5 w-full inline-flex items-center justify-center gap-2 pl-5 pr-2 py-3 rounded-full bg-[var(--accent)] text-[var(--accent-ink)] text-sm font-semibold hover:opacity-90 transition-opacity"
                    >
                        Unlock
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[var(--accent-ink)] text-[var(--accent)]">
                            <ArrowUpRight size={14} />
                        </span>
                    </button>

                    <AnimatePresence>
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: -4 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                data-testid="case-study-error"
                                className="mt-4 font-mono-brand text-[11px] uppercase tracking-[0.22em] text-red-500"
                            >
                                Incorrect passcode, try again
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <div className="mt-6 flex items-center justify-center gap-3">
                        <a
                            href={author.linkedin}
                            target="_blank"
                            rel="noreferrer"
                            data-cursor="hover"
                            aria-label="LinkedIn"
                            className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-[var(--line-2)] text-[var(--ink-2)] hover:bg-white/5 transition-colors"
                        >
                            <Linkedin size={14} />
                        </a>
                        <a
                            href={author.instagram}
                            target="_blank"
                            rel="noreferrer"
                            data-cursor="hover"
                            aria-label="Instagram"
                            className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-[var(--line-2)] text-[var(--ink-2)] hover:bg-white/5 transition-colors"
                        >
                            <Instagram size={14} />
                        </a>
                    </div>
                </motion.form>
            </div>
        </article>
    );
};

const NotFound = () => (
    <div
        data-testid={CASE_STUDY.notFound}
        className="min-h-[70vh] flex flex-col items-center justify-center px-6 text-center"
    >
        <span className="chip mb-6">404 · Case study</span>
        <h1 className="font-display text-5xl md:text-7xl leading-[0.95] text-[var(--ink)] max-w-2xl">
            This project doesn&apos;t exist.
        </h1>
        <Link
            to="/"
            className="mt-8 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[var(--accent)] text-[var(--accent-ink)] text-sm font-medium"
        >
            <ArrowLeft size={14} /> Back to work
        </Link>
    </div>
);

const GalleryImage = ({ src, i }) => {
    const [failed, setFailed] = useState(false);
    if (failed) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-10%' }}
                transition={{ duration: 0.5 }}
                data-testid={`gallery-img-missing-${i}`}
                className="mb-3 md:mb-4 rounded-2xl border border-dashed border-[var(--line-2)] bg-[var(--surface)] p-8 md:p-10 flex items-center gap-4"
            >
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-[var(--line-2)] text-[var(--ink-3)] shrink-0">
                    <Eye size={16} />
                </span>
                <div>
                    <div className="font-display-italic text-xl md:text-2xl text-[var(--ink-2)]">
                        Image missing.
                    </div>
                    <div className="mt-1 font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                        Original source unavailable · re-upload via /admin
                    </div>
                </div>
            </motion.div>
        );
    }
    return (
        <motion.figure
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-10%' }}
            transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
            className="mb-3 md:mb-4"
        >
            <div className="rounded-2xl overflow-hidden border border-[var(--line)] bg-[var(--surface)]">
                <img
                    src={proxyImg(src)}
                    alt=""
                    loading="lazy"
                    onError={() => setFailed(true)}
                    data-testid={CASE_STUDY.galleryImg(i)}
                    className="block w-full h-auto"
                />
            </div>
        </motion.figure>
    );
};


const RichBody = ({ cs }) => (
    <div data-testid="case-study-rich-body">
        {cs.cover && (
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
                className="relative mb-8 md:mb-12 rounded-[22px] overflow-hidden border border-[var(--line)] bg-[var(--surface)]"
            >
                <img
                    src={proxyImg(cs.cover)}
                    alt={`${cs.title} cover`}
                    data-testid={CASE_STUDY.cover}
                    className="block w-full h-auto"
                />
                {cs.conceptRibbon && (
                    <div
                        data-testid="case-study-concept-ribbon"
                        className="absolute top-3 left-3 md:top-4 md:left-4 flex items-center gap-2 rounded-full border border-white/15 bg-black/55 backdrop-blur-md px-3 py-1.5 shadow-lg"
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-[var(--accent)] animate-pulse" />
                        <span className="font-mono-brand text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-white/85">
                            Concept · not yet shipped
                        </span>
                    </div>
                )}
            </motion.div>
        )}

        {/* Meta card, client, timeline, responsibilities */}
        {(cs.client || cs.timeline || cs.responsibilities) && (
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="mb-8 md:mb-12 rounded-[22px] border border-[var(--line)] bg-[var(--surface)] p-5 md:p-6 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-4"
            >
                {cs.client && (
                    <div>
                        <div className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                            Client
                        </div>
                        <div className="mt-1.5 font-display-italic text-xl md:text-2xl text-[var(--ink)]">
                            {cs.client}
                        </div>
                    </div>
                )}
                {cs.timeline && (
                    <div>
                        <div className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                            Timeline
                        </div>
                        <div className="mt-1.5 font-display-italic text-xl md:text-2xl text-[var(--ink)]">
                            {cs.timeline}
                        </div>
                    </div>
                )}
                {cs.responsibilities && (
                    <div>
                        <div className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                            Responsibilities
                        </div>
                        <ul className="mt-1.5 flex flex-col gap-1">
                            {cs.responsibilities.map((r, j) => (
                                <li
                                    key={j}
                                    className="font-sans-brand text-[12.5px] text-[var(--ink-2)] flex gap-2"
                                >
                                    <span className="text-[var(--accent)]">·</span>
                                    <span>{r}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </motion.div>
        )}
        {cs.overview && (
            <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9 }}
                className="mb-8 md:mb-12 font-sans-brand text-[15.5px] md:text-[17px] leading-[1.7] text-[var(--ink-2)]"
            >
                {cs.overview}
            </motion.p>
        )}

        {cs.blocks.map((b, i) => (
            <RichBlock key={i} block={b} />
        ))}

        {(cs.prototype || cs.prototypes) && (
            <div className="my-10 md:my-14 flex flex-wrap items-center justify-center gap-3">
                {(cs.prototypes || [{ label: 'View the Figma prototype', href: cs.prototype }]).map((p, i) => (
                    <a
                        key={p.href || i}
                        href={p.href}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={i === 0 ? CASE_STUDY.prototypeLink : `${CASE_STUDY.prototypeLink}-${i}`}
                        data-cursor="hover"
                        className="inline-flex items-center gap-3 pl-6 pr-3 py-3 rounded-full bg-[var(--accent)] text-[var(--accent-ink)] text-sm font-semibold hover:opacity-90 transition-opacity"
                    >
                        {p.label}
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[var(--accent-ink)] text-[var(--accent)]">
                            <ExternalLink size={14} />
                        </span>
                    </a>
                ))}
            </div>
        )}
    </div>
);

const AuthorCard = () => (
    <div className="rounded-[22px] border border-[var(--line)] bg-[var(--surface)] p-6 md:p-8 flex items-center justify-between gap-6 flex-wrap">
        <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-[var(--accent)] text-[var(--accent-ink)] flex items-center justify-center font-display text-2xl">
                H
            </div>
            <div>
                <div className="font-sans-brand font-medium text-[var(--ink)]">
                    {author.name}
                </div>
                <div className="font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink-3)] mt-1">
                    Designer · Author
                </div>
            </div>
        </div>
        <div className="flex items-center gap-2">
            <a
                href={author.linkedin}
                target="_blank"
                rel="noreferrer"
                data-cursor="hover"
                aria-label="LinkedIn"
                className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[var(--line-2)] text-[var(--ink-2)] hover:bg-white/5 transition-colors"
            >
                <Linkedin size={15} />
            </a>
            <a
                href={author.instagram}
                target="_blank"
                rel="noreferrer"
                data-cursor="hover"
                aria-label="Instagram"
                className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[var(--line-2)] text-[var(--ink-2)] hover:bg-white/5 transition-colors"
            >
                <Instagram size={15} />
            </a>
        </div>
    </div>
);

const NextCta = ({ cs, nav }) => (
    <button
        type="button"
        onClick={() => nav(`/work/${cs.next.slug}`)}
        data-testid={CASE_STUDY.nextCta}
        data-cursor="hover"
        className="w-full h-full group rounded-[22px] border border-[var(--line)] bg-[var(--surface)] p-6 md:p-8 text-left hover:border-[var(--line-2)] transition-colors"
    >
        <div className="font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink-3)] mb-4 flex items-center justify-end gap-2">
            Next project
            <span className="w-6 h-[1px] bg-[var(--line-2)]" />
        </div>
        <div className="flex items-center gap-4">
            <div className="flex-1 min-w-0 break-words font-display text-xl md:text-4xl leading-tight text-[var(--ink)] group-hover:translate-x-2 transition-transform duration-500">
                {cs.next.title}
            </div>
            <span className="inline-flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-full bg-[var(--accent)] text-[var(--accent-ink)] shrink-0">
                <ArrowUpRight size={22} />
            </span>
        </div>
    </button>
);

// Compute the previous case-study by walking the `next` chain that lives on
// each case study. If nothing points to us (i.e. we're the head of the chain)
// wrap around to the last study in the chain, same UX as `next` cycling.
const findPrevCaseStudy = (currentSlug) => {
    const all = Object.values(caseStudies);
    const direct = all.find((c) => c.next && c.next.slug === currentSlug);
    if (direct) return { slug: direct.slug, title: direct.title };
    // No predecessor found, walk `next` from `currentSlug` until we cycle back
    // to it. The step just before we return home is the tail of the chain.
    let walker = caseStudies[currentSlug];
    let last = walker;
    const guard = all.length + 2; // avoid infinite loops on malformed data
    for (let i = 0; i < guard && walker && walker.next; i += 1) {
        const nextStudy = caseStudies[walker.next.slug];
        if (!nextStudy || nextStudy.slug === currentSlug) break;
        last = nextStudy;
        walker = nextStudy;
    }
    return last && last.slug !== currentSlug
        ? { slug: last.slug, title: last.title }
        : null;
};

const PrevCta = ({ prev, nav }) => {
    if (!prev) return null;
    return (
        <button
            type="button"
            onClick={() => nav(`/work/${prev.slug}`)}
            data-testid="case-study-prev-cta"
            data-cursor="hover"
            className="w-full h-full group rounded-[22px] border border-[var(--line)] bg-[var(--surface)] p-6 md:p-8 text-left hover:border-[var(--line-2)] transition-colors"
        >
            <div className="font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink-3)] mb-4 flex items-center gap-2">
                <span className="w-6 h-[1px] bg-[var(--line-2)]" />
                Previous project
            </div>
            <div className="flex items-center gap-4">
                <span className="inline-flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-full border border-[var(--line-2)] text-[var(--ink)] group-hover:bg-[var(--surface-2)] group-hover:border-[var(--ink)] transition-colors shrink-0">
                    <ArrowLeft size={22} />
                </span>
                <div className="flex-1 min-w-0 break-words font-display text-xl md:text-4xl leading-tight text-[var(--ink)] text-left md:text-right group-hover:-translate-x-2 transition-transform duration-500">
                    {prev.title}
                </div>
            </div>
        </button>
    );
};

// ── Body renderers ──────────────────────────────────────────────────────────
// When admin attachments exist for a case-study slug, they take precedence and
// the (now-dead) hardcoded gallery is skipped. This lets the admin fully rebuild
// a case-study visual set via /admin without any code changes.

const GalleryBody = ({ cs, attachments = [] }) => {
    const hasAttachments = attachments.length > 0;
    const attachmentImages = attachments.filter((a) =>
        (a.content_type || '').startsWith('image/')
    );

    return (
        <>
            {hasAttachments
                ? attachmentImages.map((a, i) => (
                      <GalleryImage
                          key={a.id}
                          src={`${process.env.REACT_APP_BACKEND_URL}${a.url}`}
                          i={i}
                      />
                  ))
                : cs.gallery?.map((src, i) => (
                      <GalleryImage key={src} src={src} i={i} />
                  ))}

            {cs.prototype && (
                <div className="my-10 md:my-14 flex justify-center">
                    <a
                        href={cs.prototype}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={CASE_STUDY.prototypeLink}
                        data-cursor="hover"
                        className="inline-flex items-center gap-3 pl-6 pr-3 py-3 rounded-full bg-[var(--accent)] text-[var(--accent-ink)] text-sm font-semibold hover:opacity-90 transition-opacity"
                    >
                        Prototype Link
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[var(--accent-ink)] text-[var(--accent)]">
                            <ExternalLink size={14} />
                        </span>
                    </a>
                </div>
            )}

            {!hasAttachments &&
                cs.afterPrototype?.map((src, i) => (
                    <GalleryImage
                        key={src}
                        src={src}
                        i={(cs.gallery?.length || 0) + i}
                    />
                ))}
        </>
    );
};

const ExternalBody = ({ cs }) => (
    <>
        {cs.cover && (
            <div className="rounded-[22px] overflow-hidden border border-[var(--line)] bg-[var(--surface)] mb-8">
                <img
                    src={proxyImg(cs.cover)}
                    alt={`${cs.title} cover`}
                    data-testid={CASE_STUDY.cover}
                    loading="lazy"
                    className="block w-full h-auto"
                />
            </div>
        )}
        <div className="rounded-[22px] border border-[var(--accent)] bg-[var(--accent)] text-[var(--accent-ink)] p-8 md:p-10 flex items-center justify-between gap-6 flex-wrap">
            <div>
                <div className="font-mono-brand text-[10.5px] uppercase tracking-[0.22em] opacity-70 mb-2">
                    Full case study
                </div>
                <div className="font-display text-3xl md:text-5xl leading-tight">
                    {cs.external.label}
                </div>
            </div>
            <a
                href={cs.external.href}
                target="_blank"
                rel="noreferrer"
                data-testid={CASE_STUDY.prototypeLink}
                data-cursor="hover"
                className="inline-flex items-center gap-3 pl-6 pr-2 py-3 rounded-full bg-[var(--accent-ink)] text-[var(--accent)] text-sm font-semibold hover:opacity-90 transition-opacity"
            >
                {cs.external.cta || 'Open on Behance'}
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[var(--accent)] text-[var(--accent-ink)]">
                    <ExternalLink size={14} />
                </span>
            </a>
        </div>
    </>
);

const ProtectedBody = ({ cs }) => (
    <>
        {cs.cover && (
            <div className="rounded-[22px] overflow-hidden border border-[var(--line)] bg-[var(--surface)] mb-8">
                <img
                    src={proxyImg(cs.cover)}
                    alt={`${cs.title} cover`}
                    data-testid={CASE_STUDY.cover}
                    loading="lazy"
                    className="block w-full h-auto"
                />
            </div>
        )}
        <div className="rounded-[22px] border border-[var(--line-2)] bg-[var(--surface)] p-8 md:p-12 text-center">
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[var(--surface-2)] border border-[var(--line-2)] text-[var(--accent)] mb-6">
                <Lock size={20} />
            </span>
            <div className="font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink-3)] mb-3">
                Content protected
            </div>
            <h2 className="font-display text-3xl md:text-5xl leading-[1.1] text-[var(--ink)] max-w-2xl mx-auto">
                This case study is behind a password on the source site.
            </h2>
            <p className="mt-4 max-w-md mx-auto text-[15px] text-[var(--ink-2)]">
                Reach out via LinkedIn or Instagram to request access, or open the
                original page below.
            </p>
            <div className="mt-8 flex items-center gap-3 flex-wrap justify-center">
                <a
                    href={cs.sourceUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={CASE_STUDY.prototypeLink}
                    data-cursor="hover"
                    className="inline-flex items-center gap-3 pl-5 pr-2 py-2.5 rounded-full bg-[var(--accent)] text-[var(--accent-ink)] text-sm font-semibold hover:opacity-90 transition-opacity"
                >
                    Open source page
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[var(--accent-ink)] text-[var(--accent)]">
                        <ExternalLink size={14} />
                    </span>
                </a>
                <a
                    href={author.linkedin}
                    target="_blank"
                    rel="noreferrer"
                    data-cursor="hover"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[var(--line-2)] text-sm text-[var(--ink)] hover:bg-white/5 transition-colors"
                >
                    Request access
                </a>
            </div>
        </div>
    </>
);

const NoteBody = ({ cs }) => (
    <>
        <div className="rounded-[22px] border border-[var(--line)] bg-[var(--surface)] p-8 md:p-10 mb-8">
            <div className="font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink-3)] mb-4">
                Note from the designer
            </div>
            <div className="font-display text-3xl md:text-5xl leading-tight text-[var(--ink)]">
                {cs.greeting}
            </div>
            <p className="mt-5 font-sans-brand text-lg text-[var(--ink-2)] leading-relaxed">
                {cs.note}
            </p>
            {cs.closing && (
                <ul className="mt-6 space-y-1 font-sans-brand text-[15px] text-[var(--ink-2)]">
                    {cs.closing.map((c) => (
                        <li key={c}>{c}</li>
                    ))}
                </ul>
            )}
        </div>
        {cs.gallery?.map((src, i) => (
            <GalleryImage key={src} src={src} i={i} />
        ))}
    </>
);

// ────────────────────────────────────────────────────────────────────────────

const CaseStudy = () => {
    const { slug } = useParams();
    const nav = useNavigate();
    const cs = caseStudies[slug];
    const { unlocked, unlock } = useCaseUnlock();
    const [attachments, setAttachments] = useState([]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    useEffect(() => {
        if (!slug || !unlocked) return;
        let alive = true;
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/case-studies/${slug}/attachments`;
        fetch(url)
            .then((r) => (r.ok ? r.json() : []))
            .then((data) => {
                if (alive) setAttachments(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                if (alive) setAttachments([]);
            });
        return () => {
            alive = false;
        };
    }, [slug, unlocked]);

    if (!cs) return <NotFound />;

    // Per-page SEO, unique title/description/canonical for each case study.
    const seoTitle = `${cs.title} · Case Study by Himanshu Tanty`;
    const rawDesc = cs.subtitle
        ? `${cs.subtitle}. ${cs.overview || ''}`
        : cs.overview || cs.note;
    const seoDesc =
        clampDescription(rawDesc) ||
        `${cs.title}, a ${cs.category || 'design'} case study by Himanshu Tanty, senior product designer across enterprise, AI and SaaS.`;
    const seo = (
        <Seo
            title={seoTitle}
            description={seoDesc}
            canonical={`${SITE_URL}/work/${slug}`}
            image={cs.cover ? `${SITE_URL}${cs.cover}` : undefined}
            jsonLd={caseStudySchema(cs, slug)}
        />
    );

    if (!unlocked)
        return (
            <>
                {seo}
                <Gate
                    title={cs.title}
                    subtitle={cs.subtitle}
                    category={cs.category}
                    no={cs.no}
                    overview={cs.overview}
                    timeline={cs.timeline}
                    client={cs.client}
                    responsibilities={cs.responsibilities}
                    onUnlock={unlock}
                />
            </>
        );

    return (
        <CaseStudyChapterProvider>
            {seo}
            <article data-testid={CASE_STUDY.page} className="relative pt-24 md:pt-28 pb-16">
                <ReadingProgress />
                {/* Back bar */}
            <div className="mx-auto max-w-[1100px] px-4 md:px-8 mb-6 md:mb-10 flex flex-col items-start gap-3 lg:flex-row lg:items-center lg:justify-between">
                <Link
                    to="/work"
                    data-testid={CASE_STUDY.backHome}
                    data-cursor="hover"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--line-2)] text-[13px] text-[var(--ink)] hover:bg-white/5 transition-colors shrink-0 whitespace-nowrap"
                >
                    <ArrowLeft size={14} /> Back to work
                </Link>
                <div className="flex flex-wrap items-center gap-2">
                    <span
                        data-testid={CASE_STUDY.metaRole}
                        className="chip"
                    >
                        {cs.category}
                    </span>
                    {(() => {
                        const mins = estimateReadingMinutes(cs);
                        return mins ? (
                            <span
                                className="chip"
                                data-testid="case-study-read-time"
                                title="Estimated reading time"
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-[var(--ink-2)]" />
                                ~{mins} min read
                            </span>
                        ) : null;
                    })()}
                    <span className="chip">
                        <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)]" />
                        Case study
                    </span>
                </div>
            </div>

            {/* Title */}
            <header className="mx-auto max-w-[1100px] px-4 md:px-8 mb-8 md:mb-12 text-center">
                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9 }}
                    data-testid={CASE_STUDY.title}
                    className="font-display leading-[0.92] tracking-tight text-5xl md:text-7xl lg:text-[6.5vw] text-[var(--ink)]"
                >
                    {cs.title.split(' ').slice(0, -1).join(' ') || cs.title}
                    {cs.title.split(' ').length > 1 && (
                        <>
                            {' '}
                            <span className="font-display-italic text-[var(--accent)]">
                                {cs.title.split(' ').slice(-1)[0]}
                            </span>
                        </>
                    )}
                </motion.h1>
                {cs.subtitle && (
                    <motion.p
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.15 }}
                        data-testid="case-study-subtitle"
                        className="mt-5 md:mt-7 font-display-italic text-xl md:text-3xl text-[var(--ink-2)]"
                    >
                        {cs.subtitle}
                    </motion.p>
                )}
            </header>

            {/* Body, routes by type. Attachments (when present) replace the
                dead hardcoded gallery for the 'gallery' type. */}
            <section className="mx-auto max-w-[1100px] px-4 md:px-8">
                {cs.type === 'gallery' && (
                    <GalleryBody cs={cs} attachments={attachments} />
                )}
                {cs.type === 'rich' && <RichBody cs={cs} />}
                {cs.type === 'external' && <ExternalBody cs={cs} />}
                {cs.type === 'protected' && <ProtectedBody cs={cs} />}
                {cs.type === 'note' && <NoteBody cs={cs} />}
            </section>

            {/* Non-image attachments (PDFs, files) shown as a linkable list */}
            {attachments.filter((a) => !(a.content_type || '').startsWith('image/')).length > 0 && (
                <section
                    data-testid="case-study-attachments"
                    className="mx-auto max-w-[1100px] px-4 md:px-8 mt-10 md:mt-14"
                >
                    <div className="flex items-center gap-3 mb-5 font-mono-brand text-[10.5px] uppercase tracking-[0.24em] text-[var(--ink-3)]">
                        <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)]" />
                        <span>Additional files</span>
                    </div>
                    {attachments
                        .filter((a) => !(a.content_type || '').startsWith('image/'))
                        .map((a) => {
                            const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${a.url}`;
                            return (
                                <a
                                    key={a.id}
                                    href={fullUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-cursor="hover"
                                    className="mb-3 md:mb-4 flex items-center justify-between gap-4 rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-5 hover:border-[var(--accent)] transition-colors"
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-[var(--line-2)] text-[var(--accent)]">
                                            <ExternalLink size={13} />
                                        </span>
                                        <div>
                                            <div className="font-sans-brand text-sm text-[var(--ink)]">
                                                {a.filename}
                                            </div>
                                            <div className="mt-0.5 font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
                                                {a.content_type || 'file'}
                                            </div>
                                        </div>
                                    </div>
                                    <ArrowUpRight size={16} className="text-[var(--ink-3)]" />
                                </a>
                            );
                        })}
                </section>
            )}

            {/* Author + prev/next */}
            <section className="mx-auto max-w-[1100px] px-4 md:px-8 mt-12 md:mt-16 space-y-6">
                <AuthorCard />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    <PrevCta prev={findPrevCaseStudy(cs.slug)} nav={nav} />
                    <NextCta cs={cs} nav={nav} />
                </div>
            </section>
            </article>
        </CaseStudyChapterProvider>
    );
};

export default CaseStudy;
